import React, { useState, useMemo } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { SelectableGroup, createSelectable, SelectAll, DeselectAll } from 'react-selectable-fast';
import DefaultButton from 'components/Utils/NewDefaultButton';
import StackItemsView from 'components/Common/StackItemsView';
import DocumentDetails from 'components/HealthRecords/Documents/DocumentDetails';
import ScrollPositionManager from 'components/Utils/ScrollPositionManager';
import ProgressMessage from 'components/Utils/ProgressMessage/index';
import Icon from 'components/Utils/NewIcon';
import './style.scss';
import PatientChip from 'components/Patient/PatientChip';
import ErrorBoundary from 'components/Utils/ErrorBoundary';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

const EditDocumentView = (props) => {
  //data
  let {
    document,
    stack,
    onClose,
    url,
    pages,
    lastClicked,
    isPanelOpen,
    loading,
    loadingDocument,
    selected,
    rotation,
    defaultRotations,
    pagesWithActions,
    overridedPatient,
  } = props;

  //functions
  let {
    handleSelectionFinish,
    addStackItem,
    saveChanges,
    deleteStackItem,
    handleSelectionClear,
    showPanel,
    onDocumentLoadSuccess,
    addRotation,
    deletePage,
    onPageLoadSuccess,
    onSelectPatient,
  } = props;

  return (
    <div className="edit-document rounded">
      <div className="top-row">
        <span className="large-title">{document.title}</span>
        {onClose !== undefined && <Icon className="white" src="xmark" onClick={() => onClose()} />}
      </div>
      <div className="content">
        {loadingDocument && (
          <div className="loading">
            <ProgressMessage size={120} thickness={1} />
            <span className="center-text">Loading document...</span>
          </div>
        )}
        <Document file={url} onLoadError={console.error} onLoadSuccess={onDocumentLoadSuccess}>
          {!loadingDocument && (
            <>
              <LeftColumn
                document={document}
                pagesWithActions={pagesWithActions}
                pages={pages}
                handleSelectionFinish={handleSelectionFinish}
                handleSelectionClear={handleSelectionClear}
                rotation={rotation}
                stack={stack}
                addRotation={addRotation}
                deletePage={deletePage}
                defaultRotations={defaultRotations}
                onPageLoadSuccess={onPageLoadSuccess}
              />
              <CenterColumn
                document={document}
                lastClicked={lastClicked}
                selected={selected}
                rotation={rotation}
                defaultRotations={defaultRotations}
                onPageLoadSuccess={onPageLoadSuccess}
                onSelectPatient={onSelectPatient}
              />
              <RightColumn
                document={document}
                stack={stack}
                onSave={saveChanges}
                onDeleteStackItem={deleteStackItem}
                addStackItem={addStackItem}
                showPanel={showPanel}
                isPanelOpen={isPanelOpen}
                rotation={rotation}
                selected={selected}
                overridedPatient={overridedPatient}
              />
            </>
          )}
        </Document>
      </div>
      <BottomRow
        stack={stack}
        selected={selected}
        onApplyActions={saveChanges}
        showPanel={showPanel}
        isPanelOpen={isPanelOpen}
        addStackItem={addStackItem}
        loading={loading}
      />
    </div>
  );
};

const BottomRow = (props) => (
  <div className="bottom-row">
    <DefaultButton
      name="Create New Document From Selection"
      disabled={props.selected.length === 0 || props.isPanelOpen !== false}
      onClick={() => props.showPanel('details')}
      style={{ width: '300px' }}
    />
    <DefaultButton
      name="Delete Pages"
      className="red"
      disabled={props.selected.length === 0 || props.isPanelOpen !== false}
      onClick={() => props.addStackItem('delete')}
    />
    <div className="button-right">
      <DefaultButton
        name="Apply Actions"
        disabled={props.stack.length === 0}
        onClick={props.onApplyActions}
        className="apply-actions blue"
        loading={props.loading}
      />
    </div>
  </div>
);

const CenterColumn = (props) => {
  let [zoom, setZoom] = useState(false);

  return (
    <div className="center-column">
      {props.selected.length > 0 && (
        <>
          <div className="row">
            <span className="small-title">
              {props.selected.length} Pages Selected ({getPagesDescription(props.selected)})
              <Icon
                src="magnifying-glass"
                active={zoom}
                alt="Zoom"
                showTooltip={true}
                width="20px"
                onClick={() => setZoom(!zoom)}
              />
            </span>
          </div>
          <div className="thumbnails">
            {props.selected.map((page) => (
              <MyPage
                key={page.number}
                defaultRotations={props.defaultRotations}
                page={page}
                width={zoom ? 750 : 500}
                rotation={getRotationDegrees(props.rotation, page.number)}
                patientId={
                  (
                    (props.document.ocr_patient_ids || []).find(
                      (ocrResult) => ocrResult.page + 1 === page.number,
                    ) || {}
                  ).id
                }
                onSelectPatient={props.onSelectPatient}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const LeftColumn = ({
  pages = [],
  pagesWithActions = [],
  rotation,
  setRotation,
  addRotation,
  defaultRotations,
  onPageLoadSuccess,
  document,
  deletePage,
  ...props
}) => {
  const INCREMENT = 20;

  const [limit, setLimit] = useState(INCREMENT);

  const filteredPages = useMemo(() => {
    return pages.slice(0, limit);
  }, [pages, limit]);

  return (
    <div className="left-column">
      <SelectableGroup
        key={props.stack.length}
        className="main"
        clickClassName="tick"
        tolerance={0}
        allowClickWithoutSelected={true}
        onSelectionClear={props.handleSelectionClear}
        onSelectionFinish={props.handleSelectionFinish}
        ignoreList={['.icon, button']}
      >
        <DeselectAll className="selectable-button">
          <DefaultButton name="Clear" style={{ width: '60px' }} />
        </DeselectAll>
        <SelectAll className="selectable-button">
          <DefaultButton name="Select All" style={{ width: '120px' }} />
        </SelectAll>
        <ScrollPositionManager scrollKey="some-list-key">
          {({ connectScrollTarget, ...props }) => (
            <div className="scroll-list" ref={connectScrollTarget}>
              {filteredPages.map((page) => {
                let hasAction = pagesWithActions.find((p) => p === page.number) !== undefined;

                return (
                  <SelectableMyPage
                    page={page}
                    key={page.number}
                    addRotation={addRotation}
                    deletePage={hasAction ? undefined : deletePage}
                    rotation={getRotationDegrees(rotation, page.number)}
                    defaultRotations={defaultRotations}
                    onPageLoadSuccess={onPageLoadSuccess}
                    hasAction={hasAction}
                    patientId={
                      (
                        (document.ocr_patient_ids || []).find(
                          (ocrResult) => ocrResult.page + 1 === page.number,
                        ) || {}
                      ).id
                    }
                    thumbnail={true}
                  />
                );
              })}
              {filteredPages.length !== pages.length && (
                <DefaultButton
                  name="Load more"
                  style={{ margin: '10px', width: 'calc(100% - 20px)' }}
                  onClick={() => setLimit(limit + INCREMENT)}
                />
              )}
            </div>
          )}
        </ScrollPositionManager>
      </SelectableGroup>
    </div>
  );
};

const RightColumn = (props) => {
  let className = 'right-column';
  if (props.isPanelOpen === 'details') className += ' expanded';

  const [document] = useState({
    ...props.document,
    filename: undefined,
    id: undefined,
  });

  return (
    (props.isPanelOpen !== false || props.stack.length > 0) && (
      <div className={className}>
        {props.isPanelOpen === 'details' && (
          <div className="details-panel">
            <DocumentDetails
              overridedPatient={props.overridedPatient}
              document={document}
              disableEdit={false}
              onSave={(document) => props.addStackItem('add', document)}
              onCancel={() => props.showPanel('details', false)}
              hideEditDocument={true}
              allowLink={true}
              disableSend={true}
              alwaysAllowSave={true}
            />
          </div>
        )}
        {props.stack.length > 0 && !props.isPanelOpen && (
          <>
            <span className="small-title">Actions</span>
            <StackItemsView
              stack={props.stack}
              onDeleteItem={props.onDeleteStackItem}
              describeItem={describeStackItem}
            />
          </>
        )}
      </div>
    )
  );
};

const MyPage = ({
  page,
  selected,
  selecting,
  selectableRef,
  onClick,
  width = 150,
  rotation = 0,
  addRotation,
  defaultRotations = [],
  onPageLoadSuccess,
  renderMode = 'canvas',
  hasAction = false,
  patientId,
  thumbnail = false,
  onSelectPatient,
  deletePage,
}) => {
  let className = 'page-wrapper clickable';
  if (selected || selecting) className += ' selected';
  if (hasAction) className += ' has-action';

  return (
    <ErrorBoundary>
      <div ref={selectableRef} className={className} onClick={onClick}>
        {patientId && <PatientChip id={patientId} short={thumbnail} onClick={onSelectPatient} />}
        {addRotation !== undefined && (
          <Icon
            src="arrow-rotate-right"
            className="blue"
            width="20px"
            onClick={() => addRotation(page.number, 90)}
            style={{
              position: 'absolute',
              right: '30px',
              top: '5px',
              zIndex: 1,
            }}
          />
        )}
        {deletePage !== undefined && (
          <Icon
            src="trash"
            fontAwesomeStyle="fas"
            width="20px"
            className="red"
            onClick={() => deletePage(page)}
            style={{
              position: 'absolute',
              right: '30px',
              bottom: 0,
              zIndex: 1,
            }}
          />
        )}
        <span className="page-number">{page.number}</span>
        <Page
          renderMode={renderMode}
          onLoadSuccess={onPageLoadSuccess}
          key={page.number}
          pageNumber={page.number}
          width={width}
          rotate={((defaultRotations[page.number] || 0) + rotation) % 360}
        />
      </div>
    </ErrorBoundary>
  );
};
const SelectableMyPage = createSelectable(MyPage);

function getPagesDescription(pages = []) {
  let numbers = pages.map((page) => page.number);
  // Return fast if array is null or contains less than 2 items
  if (numbers.length === 0) return '';
  if (numbers.length === 1) return numbers[0];

  let rangeString = '';
  let isRange = false;

  for (let i = 0; i < numbers.length; i++) {
    while (i < numbers.length - 1 && numbers[i] + 1 === numbers[i + 1]) {
      if (!isRange) rangeString += numbers[i];
      isRange = true;
      i++;
    }

    if (isRange) {
      rangeString += '-';
      isRange = false;
    }

    rangeString += numbers[i] + ',';
  }

  if (rangeString.charAt(rangeString.length - 1) === ',')
    rangeString = rangeString.substr(0, rangeString.length - 1);

  return rangeString;
}

function describeStackItem(stackItem) {
  let description = `${stackItem.pages.length} pages (${getPagesDescription(
    stackItem.pages,
  )}) to be `;

  if (stackItem.document && stackItem.document.document_type) {
    description = `${stackItem.document.document_type} with ${description}`;
  }

  return description;
}

function getRotationDegrees(rotations = [], page) {
  let rotation = rotations.filter((rotation) => {
    return rotation.page === page;
  })[0];
  return (rotation || {}).degrees;
}

export default EditDocumentView;
